import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@domcrm/embed-ui';

import { getMeOnboarding, onboardingPending } from '../widget-api/auth-onboarding';
import { getMe, postRequestEmailVerification } from '../widget-api/auth-me';
import { ApiError } from '../widget-api/http-client';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { clearEmbedSession, clearGlobalEmbedSession } from '../widget-lib/embed-session-store';
import { PATH } from '../widget-lib/path-routes';
import { EmbedMessageBlock } from '../widget-ui/embed-message-block';

export default function ConfirmEmailPage({ afterVerifyPath }: { afterVerifyPath?: string } = {}) {
  useWidgetSeo(
    'Подтверждение почты',
    'Страница подтверждения электронного адреса клиента.'
  );
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const suffix = useQuerySuffix();
  const { accessToken, me, meLoading, reloadMe, orgSlug, organizationId } = useEmbedBootstrap();
  const [resendBusy, setResendBusy] = useState(false);
  const [resendNote, setResendNote] = useState<string | null>(null);
  const [verifyCheckBusy, setVerifyCheckBusy] = useState(false);
  const [cooldownSec, setCooldownSec] = useState(0);

  /** В глобальном приложении без выбранной организации — после подтверждения идём в каталог, а не к расписанию. */
  const isGlobalNoOrg = searchParams.get('embed_global') === '1' && !orgSlug;
  /** Куда идти после подтверждения: клиент (каталог/расписание) или тренерский режим. */
  const defaultDest = afterVerifyPath ?? (isGlobalNoOrg ? PATH.catalog : PATH.schedule);


  useEffect(() => {
    if (!accessToken || me?.email_verified_at) return;
    const interval = setInterval(async () => {
      try {
        const fresh = await getMe(accessToken);
        if (fresh.email_verified_at) {
          reloadMe();
          const ob = await getMeOnboarding(organizationId, accessToken);
          const dest = onboardingPending(ob) ? PATH.agreements : defaultDest;
          navigate(`${dest}${suffix}`, { replace: true });
        }
      } catch {
        // ignore polling errors
      }
    }, 10_000);
    return () => clearInterval(interval);
  }, [accessToken, me?.email_verified_at, organizationId, reloadMe, navigate, defaultDest, suffix]);

  function startCooldown(sec: number) {
    setCooldownSec(sec);
    if (sec <= 0) return;
    const id = setInterval(() => {
      setCooldownSec((prev) => {
        if (prev <= 1) { clearInterval(id); return 0; }
        return prev - 1;
      });
    }, 1000);
  }

  if (!accessToken) {
    return (
      <div className="mx-auto w-full max-w-md space-y-3 min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Подтверждение email</h1>
        <p className="text-sm text-[var(--text-muted)]">Войдите, чтобы подтвердить email.</p>
      </div>
    );
  }

  if (meLoading) {
    return (
      <div className="mx-auto w-full max-w-md min-w-0">
        <p className="text-sm text-[var(--text-muted)]">Загрузка…</p>
      </div>
    );
  }

  if (me?.email_verified_at) {
    return (
      <div className="mx-auto w-full max-w-md space-y-3 min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Email подтверждён</h1>
        <p className="text-sm text-[var(--text-muted)]">Ваш адрес электронной почты уже подтверждён.</p>
        <Button type="button" onClick={() => navigate(`${defaultDest}${suffix}`)}>
          {afterVerifyPath ? 'Продолжить' : isGlobalNoOrg ? 'В каталог организаций' : 'К расписанию'}
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-md space-y-4 min-w-0">
      <h1 className="text-xl font-semibold tracking-tight">Подтвердите email</h1>
      <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] p-4 space-y-2">
        <p className="text-sm text-[var(--text)]">
          Мы отправили ссылку для подтверждения на адрес{' '}
          <span className="font-medium">{me?.email || '—'}</span>.
        </p>
        <p className="text-sm text-[var(--text-muted)]">
          Перейдите по ссылке из письма, чтобы продолжить. Проверьте папку «Спам», если письмо не пришло.
        </p>
      </div>

      {resendNote ? (
        <EmbedMessageBlock
          variant="info"
          size="field"
          className="w-full"
          role="status"
          aria-live="polite"
          showIcon={false}
        >
          {resendNote}
        </EmbedMessageBlock>
      ) : null}

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          type="button"
          disabled={resendBusy || cooldownSec > 0}
          onClick={async () => {
            if (!accessToken) return;
            setResendBusy(true);
            setResendNote(null);
            try {
              await postRequestEmailVerification(accessToken);
              setResendNote('Письмо отправлено повторно. Проверьте почту.');
              startCooldown(300);
            } catch (e) {
              const msg =
                e instanceof ApiError
                  ? e.detail
                  : e instanceof Error
                    ? e.message
                    : 'Не удалось отправить';
              setResendNote(msg);
              if (msg.includes('5 минут') || msg.includes('5 мину')) {
                startCooldown(300);
              }
            } finally {
              setResendBusy(false);
            }
          }}
        >
          {resendBusy
            ? 'Отправляем…'
            : cooldownSec > 0
              ? `Повторно через ${Math.floor(cooldownSec / 60)}:${String(cooldownSec % 60).padStart(2, '0')}`
              : 'Отправить письмо ещё раз'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={verifyCheckBusy}
          onClick={async () => {
            if (!accessToken) return;
            setVerifyCheckBusy(true);
            setResendNote(null);
            try {
              reloadMe();
              const meNow = await getMe(accessToken);
              if (!meNow.email_verified_at) {
                setResendNote('Пока не видим подтверждения. Откройте ссылку из письма и нажмите снова.');
                return;
              }
              const ob = await getMeOnboarding(organizationId, accessToken);
              if (onboardingPending(ob)) {
                navigate(`${PATH.agreements}${suffix}`, { replace: true });
                return;
              }
              const dest = defaultDest;
              navigate(`${dest}${suffix}`, { replace: true });
            } catch (e) {
              const msg =
                e instanceof ApiError
                  ? e.detail
                  : e instanceof Error
                    ? e.message
                    : 'Не удалось проверить статус';
              setResendNote(msg);
            } finally {
              setVerifyCheckBusy(false);
            }
          }}
        >
          {verifyCheckBusy ? 'Проверяем…' : 'Я подтвердил — проверить'}
        </Button>
      </div>

      <button
        type="button"
        className="text-sm text-[var(--text-muted)] underline-offset-2 hover:underline hover:text-[var(--text)]"
        onClick={() => {
          clearEmbedSession(orgSlug);
          clearGlobalEmbedSession();
          window.location.href = `/embed/sign-in${suffix}`;
        }}
      >
        Выйти и войти другим аккаунтом
      </button>
    </div>
  );
}
